import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Navigation } from "./components/Navigation";
import { Footer } from "./components/Footer";
import { Home } from "./pages/Home";
import { Services } from "./pages/Services";
import { About } from "./pages/About";
import { Contact } from "./pages/Contact";
import { ChauffeurHub } from "./pages/ChauffeurHub";
import { ChauffeurFleet } from "./pages/ChauffeurFleet";
import { ChauffeurRates } from "./pages/ChauffeurRates";
import { ChauffeurBook } from "./pages/ChauffeurBook";
import { ChauffeurServices } from "./pages/ChauffeurServices";
import { Terms } from "./pages/Terms";
import { Privacy } from "./pages/Privacy";
import { FAQ } from "./pages/FAQ";
import { Blog, BlogArticle } from "./pages/Blog";
import { AdminLogin } from "./pages/AdminLogin";
import { AdminDashboard } from "./pages/AdminDashboard";
import { AdminForgotPassword } from "./pages/AdminForgotPassword";
import { AdminResetPassword } from "./pages/AdminResetPassword";
import { Cookies } from "./pages/Cookies";
import { CookieBanner } from "./components/CookieBanner";
import { StickyMobileCTA } from "./components/StickyMobileCTA";
import { AuthProvider } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { Phone } from "lucide-react";

const SITE_URL = 'https://torontoroyale.com';
const OG_IMAGE = `${SITE_URL}/og-cover.jpg`; // 1200×630 social share card

const PAGE_SEO = {
  '/': { title: 'Toronto Royale | Premium Chauffeur Service Toronto, Oakville, Milton & GTA', description: 'Toronto Royale is a founder-led, boutique chauffeur service in Toronto, Oakville, Milton and the GTA. Executive travel, airport transfers to Pearson, luxury black car service. Book today.' },
  '/services': { title: 'Chauffeur Services Toronto — Executive Travel, Airport Transfers | Toronto Royale', description: 'Premium chauffeur services in Toronto, Oakville & Milton. Executive travel, Toronto Pearson airport transfers, family chauffeur, and special occasion transport across the GTA.' },
  '/about': { title: 'About Toronto Royale — Boutique Chauffeur Service Toronto & GTA', description: 'Toronto Royale is a founder-led, boutique chauffeur service built on trust, consistency, and genuine care. Serving Toronto, Oakville, Milton and the Greater Toronto Area.' },
  '/contact': { title: 'Book a Chauffeur in Toronto | Toronto Royale — Call (437) 996-3801', description: 'Book your premium chauffeur ride in Toronto, Oakville, or Milton. Same-day availability. Call +1 (437) 996-3801 or fill out our booking form. Response within 1 hour.' },
  '/chauffeur': { title: 'Private Chauffeur Service Toronto & GTA | Luxury SUV Transport', description: 'Discreet, on-time, luxury SUV chauffeur service for airport transfers, corporate travel, and private travel in Toronto, Oakville, Milton and the Greater Toronto Area.' },
  '/chauffeur/fleet': { title: 'Luxury Fleet — Escalade, S-Class, Suburban | Toronto Royale', description: 'Our curated fleet: Cadillac Escalade ESV, Mercedes-Benz S-Class, Chevrolet Suburban Premier. Premium luxury vehicles for chauffeur service in Toronto and the GTA.' },
  '/chauffeur/rates': { title: 'Request a Quote — Chauffeur Service Toronto | Toronto Royale', description: 'Get a personalized quote for chauffeur services in Toronto, Oakville, Milton and the GTA. Transparent pricing, no surge, no hidden fees.' },
  '/chauffeur/book': { title: 'Book a Private Chauffeur — Toronto, Oakville, Milton | Toronto Royale', description: 'Submit your chauffeur request for Toronto, Oakville, Milton or anywhere in the GTA. Same-day availability. Typical response: under 1 hour.' },
  '/chauffeur/services': { title: 'Service Areas — Airport Transfers, Corporate, Long-Distance | Toronto Royale', description: 'Chauffeur service areas: Toronto Pearson airport transfers, corporate travel, private travel, long-distance routes, and cross-border service across the GTA.' },
  '/terms': { title: 'Terms & Conditions | Toronto Royale Chauffeur Service', description: 'Terms and conditions for Toronto Royale chauffeur and luxury transportation services in Toronto and the GTA.' },
  '/privacy': { title: 'Privacy Policy | Toronto Royale Chauffeur Service', description: 'How Toronto Royale collects, uses, and protects your personal information. PIPEDA compliant.' },
  '/cookies': { title: 'Cookie Policy | Toronto Royale Chauffeur Service', description: 'What cookies Toronto Royale uses, why we use them, and how you can change your consent. PIPEDA, Quebec Law 25, and GDPR aware.' },
  '/faq': { title: 'FAQ — Chauffeur Service Toronto | Toronto Royale', description: 'Frequently asked questions about Toronto Royale chauffeur services. Booking, pricing, fleet, airport transfers, coverage areas, and more.' },
  '/blog': { title: 'Blog — Chauffeur & Executive Travel Insights | Toronto Royale', description: 'Expert guides on premium chauffeur services, airport transfers, executive travel, and luxury transportation in Toronto, Oakville, Milton and the GTA.' },
  '/blog/best-chauffeur-service-toronto': { title: 'Best Chauffeur Service in Toronto — A Boutique Alternative to Black Car Apps', description: 'What separates a true premium chauffeur service from rideshare and fleet operators in Toronto. A founder-led look at the boutique chauffeur experience.' },
  '/blog/airport-transfer-toronto-pearson': { title: 'Toronto Pearson Airport Transfer Guide — Premium Chauffeur Service', description: 'Everything you need to know about premium airport transfer to and from Toronto Pearson International Airport (YYZ) — flight tracking, meet-and-greet, luggage handling.' },
  '/blog/executive-car-service-gta': { title: 'Executive Car Service GTA — Why Boutique Chauffeurs Beat Fleet Operators', description: 'Executive car service for business travel in the Greater Toronto Area. Why busy executives are switching from fleet operators to boutique chauffeur services.' },
  '/blog/private-chauffeur-vs-uber-black-toronto': { title: 'Private Chauffeur vs Uber Black in Toronto — Which Is Right for You?', description: 'Honest, side-by-side comparison of private chauffeur service vs Uber Black in Toronto. When each makes sense for executives, families, and special occasions.' },
  '/blog/chauffeur-service-burlington': { title: 'Chauffeur Service in Burlington — Premium Lakeshore Transportation | Toronto Royale', description: 'Premium chauffeur service in Burlington, Ontario. Pearson airport transfers, Toronto business travel, Niagara wine country day trips, and family rides across Halton.' },
  '/blog/niagara-falls-chauffeur-from-toronto': { title: 'Niagara Falls Chauffeur from Toronto — Wine Tours, Day Trips, Weddings', description: 'Hire a private chauffeur for Niagara Falls and Niagara-on-the-Lake from Toronto, Oakville, or anywhere in the GTA. Wine tours, day trips, and special-occasion travel.' },
  '/blog/wedding-chauffeur-oakville': { title: 'Wedding Chauffeur in Oakville | Premium Wedding Transportation GTA', description: 'Premium wedding chauffeur service in Oakville, Burlington, and the GTA. Coordinated bridal-party transfers, formal-dress chauffeurs, multi-venue wedding logistics.' },
  '/blog/concert-chauffeur-toronto': { title: 'Concert & Event Chauffeur Toronto — Scotiabank Arena, Rogers Centre, Budweiser Stage', description: 'Private chauffeur service for Toronto concerts and events. Scotiabank Arena, Rogers Centre, Budweiser Stage. Skip parking, skip surge — fixed-price luxury rides.' },
};

function setMeta(selector, attr, value) {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    const [, key, val] = selector.match(/\[(\w+)="([^"]+)"\]/) || [];
    if (key && val) el.setAttribute(key, val);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function PageSEO() {
  const { pathname } = useLocation();
  useEffect(() => {
    const isAdmin = pathname.startsWith('/admin');
    // Robots: admin = noindex, public = index
    setMeta('meta[name="robots"]', 'content', isAdmin ? 'noindex,nofollow' : 'index,follow');
    if (isAdmin) return; // admin pages set their own title

    const seo = PAGE_SEO[pathname] || PAGE_SEO['/'];
    const url = `${SITE_URL}${pathname === '/' ? '/' : pathname}`;

    document.title = seo.title;
    setMeta('meta[name="description"]', 'content', seo.description);

    // Per-route canonical
    setLink('canonical', url);

    // Open Graph (per-route)
    setMeta('meta[property="og:title"]', 'content', seo.title);
    setMeta('meta[property="og:description"]', 'content', seo.description);
    setMeta('meta[property="og:url"]', 'content', url);
    setMeta('meta[property="og:image"]', 'content', OG_IMAGE);
    setMeta('meta[property="og:type"]', 'content', pathname.startsWith('/blog/') ? 'article' : 'website');

    // Twitter Card (per-route)
    setMeta('meta[name="twitter:title"]', 'content', seo.title);
    setMeta('meta[name="twitter:description"]', 'content', seo.description);
    setMeta('meta[name="twitter:image"]', 'content', OG_IMAGE);
  }, [pathname]);
  return null;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function PageTransition({ children }) {
  const { pathname } = useLocation();
  const [show, setShow] = useState(false);

  useEffect(() => {
    setShow(false);
    const t = setTimeout(() => setShow(true), 30);
    return () => clearTimeout(t);
  }, [pathname]);

  return (
    <div style={{
      opacity: show ? 1 : 0,
      transition: 'opacity 350ms ease-in-out',
    }}>
      {children}
    </div>
  );
}

function FloatingCallButton() {
  return (
    <a
      href="tel:+14379963801"
      data-testid="floating-call-btn"
      aria-label="Call Toronto Royale"
      style={{
        position: 'fixed',
        bottom: '56px',
        right: '32px',
        zIndex: 40,
        width: '56px',
        height: '56px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#C2A878',
        borderRadius: '50%',
        boxShadow: '0 4px 20px rgba(0,0,0,0.4), 0 0 16px rgba(194,168,120,0.2)',
        cursor: 'pointer',
        transition: 'transform 300ms ease, box-shadow 300ms ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'scale(1.08)';
        e.currentTarget.style.boxShadow = '0 6px 24px rgba(0,0,0,0.5), 0 0 24px rgba(194,168,120,0.3)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.4), 0 0 16px rgba(194,168,120,0.2)';
      }}
    >
      <Phone size={22} color="#0B0B0B" />
    </a>
  );
}

function isAdminRoute(pathname) {
  return pathname.startsWith('/admin');
}

function ConditionalChrome() {
  const { pathname } = useLocation();
  if (isAdminRoute(pathname)) return null;
  return <Navigation />;
}

function ConditionalFooter() {
  const { pathname } = useLocation();
  if (isAdminRoute(pathname)) return null;
  return <Footer />;
}

function ConditionalCallButton() {
  const { pathname } = useLocation();
  if (isAdminRoute(pathname)) return null;
  return <StickyMobileCTA />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <PageSEO />
          <ConditionalChrome />
          <main>
            <PageTransition>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/services" element={<Services />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/chauffeur" element={<ChauffeurHub />} />
                <Route path="/chauffeur/fleet" element={<ChauffeurFleet />} />
                <Route path="/chauffeur/rates" element={<ChauffeurRates />} />
                <Route path="/chauffeur/book" element={<ChauffeurBook />} />
                <Route path="/chauffeur/services" element={<ChauffeurServices />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/cookies" element={<Cookies />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/best-chauffeur-service-toronto" element={<BlogArticle slug="best-chauffeur-service-toronto" />} />
                <Route path="/blog/airport-transfer-toronto-pearson" element={<BlogArticle slug="airport-transfer-toronto-pearson" />} />
                <Route path="/blog/executive-car-service-gta" element={<BlogArticle slug="executive-car-service-gta" />} />
                <Route path="/blog/private-chauffeur-vs-uber-black-toronto" element={<BlogArticle slug="private-chauffeur-vs-uber-black-toronto" />} />
                <Route path="/blog/chauffeur-service-burlington" element={<BlogArticle slug="chauffeur-service-burlington" />} />
                <Route path="/blog/niagara-falls-chauffeur-from-toronto" element={<BlogArticle slug="niagara-falls-chauffeur-from-toronto" />} />
                <Route path="/blog/wedding-chauffeur-oakville" element={<BlogArticle slug="wedding-chauffeur-oakville" />} />
                <Route path="/blog/concert-chauffeur-toronto" element={<BlogArticle slug="concert-chauffeur-toronto" />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
                <Route path="/admin/reset-password" element={<AdminResetPassword />} />
                <Route path="/admin" element={<AdminDashboard />} />
              </Routes>
            </PageTransition>
          </main>
          <ConditionalFooter />
          <ConditionalCallButton />
          <CookieBanner />
          <Toaster />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
